import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Box, Flex, Text } from '@chakra-ui/core'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/core'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { CloseButton, EmptyState } from 'components/ui'
import { NotificationRow } from 'components/Notifications/NotificationRow'
import { NotificationRowSkeleton } from 'components/Notifications/NotificationRowSkeleton'
import {
  handleFetchInbox,
  handleFetchUnread,
  handleFetchUnreadCount,
  handleMarkSeen,
} from 'redux/actions/notifications'
import {
  notificationsInboxSelector,
  notificationsUnreadSelector,
  unreadCountSelector,
} from 'redux/selectors/notifications'
import { notifShadows, notifTokens } from 'services/notifications/designTokens'
import { groupNotificationsByDate } from 'services/notifications/groupByDate'
import { handleNotificationClick } from 'services/notifications/handleNotificationClick'
import { INotification } from 'services/notifications/types'
import { getDefaultNotificationTab, NotificationCenterTab } from 'services/notifications/defaultNotificationTab'
import { isRtlSelector } from 'redux/selectors/general'
import useLocaleText from 'components/useLocaleText'
import LocaleText from 'components/LocaleText'

interface IProps {
  isOpen: boolean
  onClose: () => void
}

// Tablet / mobile surface (spec §3) — below the 1180px breakpoint the desktop 3-pane modal
// (NotificationDesktopModal) degrades to this. It's a self-contained fixed-position portal (own
// scrim, own Escape/scroll-lock), NOT a Chakra Popover anchored to the bell — the bell lives
// inside the mobile burger-menu drawer, which is display:none'd while this is open (see
// NotificationBellButton's setIsHidden wiring), so a Popper-positioned popover would have no
// visible/measurable trigger to anchor to. Sizing degrades gracefully from tablet down to phone
// widths via `min(420px, 100vw)` — no separate mobile layout needed.
const LOAD_MORE_THRESHOLD_PX = 200

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`
// Slides in from whichever edge the panel is docked to — right in LTR, left in RTL (see
// justifyContent on Scrim below, which docks the panel to that same edge).
const slideInLtr = keyframes`
  from { transform: translateX(24px); }
  to { transform: translateX(0); }
`
const slideInRtl = keyframes`
  from { transform: translateX(-24px); }
  to { transform: translateX(0); }
`

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  background: ${notifTokens.scrim};
  z-index: 1400;
  display: flex;
  animation: ${fadeIn} 160ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Panel = styled.div<{ isRtl: boolean }>`
  width: min(420px, 100vw);
  max-width: 100vw;
  height: 100%;
  background: white;
  box-shadow: ${notifShadows.popover};
  display: flex;
  flex-direction: column;
  animation: ${({ isRtl }) => (isRtl ? slideInRtl : slideInLtr)} 200ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const CountPill: React.FC<{ count: number; isActive: boolean }> = ({ count, isActive }) => (
  <Flex
    as="span"
    minW="18px"
    h="18px"
    px="5px"
    borderRadius="999px"
    alignItems="center"
    justifyContent="center"
    fontSize="10.5px"
    fontWeight={600}
    bg={isActive ? notifTokens.ink : '#F1F5F9'}
    color={isActive ? 'white' : notifTokens.faint}
    ml="6px"
  >
    {count}
  </Flex>
)

export const NotificationCenterPanel: React.FC<IProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [tab, setTab] = useState<NotificationCenterTab>('unread')
  const listRef = useRef<HTMLDivElement>(null)
  const didUserChooseTabRef = useRef(false)

  const inbox = useSelector(notificationsInboxSelector)
  const unread = useSelector(notificationsUnreadSelector)
  const unreadCount = useSelector(unreadCountSelector)
  const isRtl = useSelector(isRtlSelector)
  const isRtlLayout = isRtl || (typeof document !== 'undefined' && document.documentElement.dir === 'rtl')
  const active = tab === 'all' ? inbox : unread

  const t_notifications = useLocaleText('mobile_menu_notifications')

  // Refetch both lists every time the panel opens, not just the first time ever — see the same
  // fix (and its rationale) in NotificationDesktopModal.tsx.
  useEffect(() => {
    if (!isOpen) {
      didUserChooseTabRef.current = false
      return
    }
    setTab(getDefaultNotificationTab(unreadCount))
    dispatch(handleFetchInbox())
    dispatch(handleFetchUnread())
    Promise.resolve(dispatch(handleFetchUnreadCount())).then((count) => {
      if (typeof count === 'number' && !didUserChooseTabRef.current) {
        setTab(getDefaultNotificationTab(count))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Esc closes; body scroll is locked while open — same shell contract as NotificationDesktopModal.
  useEffect(() => {
    if (!isOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, onClose])

  const groups = useMemo(() => groupNotificationsByDate(active.items), [active.items])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX
    if (nearBottom && active.nextCursor && !active.isLoading) {
      dispatch(tab === 'all' ? handleFetchInbox({ append: true }) : handleFetchUnread({ append: true }))
    }
  }

  const onRowClick = (notification: INotification) => {
    onClose()
    handleNotificationClick(notification, navigate, {
      dispatch,
      markSeen: (routedNotification) => {
        dispatch(handleMarkSeen({ seenKeys: [routedNotification.chrono_key] }))
      },
    }).catch((err) => console.warn('[Notifications] click handling failed:', err))
  }

  // ArrowUp/Down move focus between rows; Enter/Space activate the focused row natively (they're
  // real <button>s) — Escape-to-close is handled by the keydown listener above.
  const onListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const rows = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[data-notif-row]') ?? [])
    if (!rows.length) return
    const currentIndex = rows.indexOf(document.activeElement as HTMLButtonElement)
    const nextIndex =
      e.key === 'ArrowDown' ? Math.min(currentIndex + 1, rows.length - 1) : Math.max(currentIndex - 1, 0)
    rows[Math.max(nextIndex, 0)]?.focus()
  }

  const t_all_caught_up = useLocaleText('all_caught_up')
  const emptyCopy = t_all_caught_up

  if (!isOpen) return null
  if (typeof document === 'undefined') return null

  const node = (
    <Scrim onClick={onClose} style={{ justifyContent: isRtlLayout ? 'flex-start' : 'flex-end' }}>
      <Panel isRtl={isRtlLayout} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        {/* Header */}
        <Box position="relative" p="16px 44px 12px" flexShrink={0}>
          <Box textAlign="center">
            <Text fontSize="16px" fontWeight={600} color={notifTokens.ink}>
              {t_notifications}
            </Text>
          </Box>
          <Flex
            position="absolute"
            top="16px"
            left={isRtlLayout ? '10px' : 'auto'}
            right={isRtlLayout ? 'auto' : '10px'}
            alignItems="center"
            style={{ columnGap: '10px' }}
          >
            <CloseButton minW="28px" h="28px" p="4px" onClick={onClose} />
          </Flex>
        </Box>

        {/* Unread / All tabs */}
        <Flex px="16px" style={{ columnGap: '20px' }} borderBottom={`1px solid ${notifTokens.hairline}`} flexShrink={0}>
          {(['unread', 'all'] as const).map((t) => (
            <Flex
              key={t}
              as="button"
              onClick={() => {
                didUserChooseTabRef.current = true
                setTab(t)
              }}
              alignItems="center"
              pb="10px"
              borderBottom={tab === t ? `2px solid ${notifTokens.ink}` : '2px solid transparent'}
              fontSize="13px"
              fontWeight={tab === t ? 600 : 400}
              color={tab === t ? notifTokens.ink : notifTokens.faint}
            >
              {t === 'unread' ? 'Unread' : 'All'}
              <CountPill count={t === 'unread' ? unreadCount : inbox.items.length} isActive={tab === t} />
            </Flex>
          ))}
        </Flex>

        {/* List */}
        <Box ref={listRef} flex="1" minH="0" overflowY="auto" onScroll={handleScroll} onKeyDown={onListKeyDown} p="8px">
          {active.isLoading && !active.hasLoadedOnce && (
            <Box>
              <NotificationRowSkeleton />
              <NotificationRowSkeleton />
            </Box>
          )}

          {active.hasLoadedOnce && active.items.length === 0 && <EmptyState defaultText={emptyCopy} />}

          {groups.map((group) => (
            <Box key={group.label} mb="4px">
              <Text
                fontSize="10.5px"
                fontWeight={600}
                color={notifTokens.faint}
                px="6px"
                py="6px"
                letterSpacing="0.08em"
                textTransform="uppercase"
              >
                <LocaleText text={group.label} />
              </Text>
              {group.items.map((notification) => (
                <NotificationRow
                  key={notification.chrono_key}
                  notification={notification}
                  onClick={() => onRowClick(notification)}
                />
              ))}
            </Box>
          ))}

          {active.isLoading && active.hasLoadedOnce && (
            <Box>
              <NotificationRowSkeleton />
              <NotificationRowSkeleton />
            </Box>
          )}
        </Box>
      </Panel>
    </Scrim>
  )

  return createPortal(node, document.body)
}
